// import {
// 	useUpdatePersonContactMutation,
// } from "@repo/server-api";
import {
	useCreateDateInformation,
	useUpdateDateInformation,
	useCreatePhoneInformation,
	useUpdatePhoneInformation,
} from "@repo/legacy/components/hooks/ContactMutationHooks";
import {
	DateInformation,
	PersonDateType,
	PhoneInformation,
	PhoneType,
	useGetMeLazyQuery,
	useUpdateMyPaymentConditionMutation,
} from "@repo/server-api";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useNotifier } from "../../../shared/src/common/useNotifier";

import type { Account } from "../types/ContactTypes";


type UseAccountOptions = {
	onSaved: (account: Account) => void;
};

type UseAccountResult = {
	updateAccount: (account: Account) => void;
	loading: boolean;
};

// Here is the hook
export function useUpdateAccountHook(options: UseAccountOptions): UseAccountResult {
	const { t } = useTranslation();
	const { notifySuccess, notifyException } = useNotifier();

	const { onSaved } = options;
	const [loading, setLoading] = useState(false);

	const [getMe] = useGetMeLazyQuery();
	const [addPhoneInformation] = useCreatePhoneInformation();
	const [updatePhoneInformation] = useUpdatePhoneInformation();
	const [addDateInformation] = useCreateDateInformation();
	const [updateDateInformation] = useUpdateDateInformation();
	const [updateMyPaymentConditions] = useUpdateMyPaymentConditionMutation();

	const updateAccount = async (account: Account) => {
		setLoading(true);
		try {
			const me = await getMe();

			const oldAccount = me.data?.me;

			if (!oldAccount) throw new Error("Account not found");

			// Update account phone information
			const phoneInfo = oldAccount.additionalInformation.filter(
				(info) => info.__typename === "PhoneInformation" && info.phoneType === PhoneType.Work,
			)[0] as PhoneInformation | undefined;
			if (!phoneInfo?.number)
				await addPhoneInformation(account.id, {
					id: "",
					number: account.phone ?? "",
					phoneType: PhoneType.Work,
				});
			else
				await updatePhoneInformation(account.id, {
					id: phoneInfo.id,
					number: account.phone ?? "",
					phoneType: PhoneType.Work,
				});

			// Update account mobile information
			const mobileInfo = oldAccount.additionalInformation.filter(
				(info) => info.__typename === "PhoneInformation" && info.phoneType === PhoneType.Mobile,
			)[0] as PhoneInformation | undefined;
			if (!mobileInfo?.number)
				await addPhoneInformation(account.id, {
					id: "",
					number: account.mobile ?? "",
					phoneType: PhoneType.Mobile,
				});
			else
				await updatePhoneInformation(account.id, {
					id: mobileInfo.id,
					number: account.mobile ?? "",
					phoneType: PhoneType.Mobile,
				});

			// Update account birthday information
			const birthdayInfo = oldAccount.additionalInformation.find(
				(info) =>
					info.__typename === "DateInformation" && info.personDateType === PersonDateType.BirthDate,
			) as DateInformation | undefined;
			if (!birthdayInfo?.date && account.birthday)
				await addDateInformation(account.id, {
					id: "",
					date: account.birthday,
					personDateType: PersonDateType.BirthDate,
				});
			else if (birthdayInfo?.date && account.birthday)
				await updateDateInformation(account.id, {
					id: birthdayInfo.id,
					date: account.birthday,
					personDateType: PersonDateType.BirthDate,
				});

			// Update payment conditions if changed
			account.termsOfPaymentEntries.forEach(async (term) => {
				await updateMyPaymentConditions({
					variables: {
						contactId: term.company.id,
						contactInformationId: term.id,
						selectedEntry: term.selectedEntry?.id ?? "",
					},
				});
			});

			notifySuccess(t("account-updated"));
			onSaved(account);
		} catch (error) {
			notifyException(error);
		} finally {
			setLoading(false);
		}
	};

	return { updateAccount, loading };
}
